import { useEffect, useRef, useState } from "react";
import { api, API } from "../../lib/api";
import { GrowthSettings } from "./growthSettings";
import {
  RefreshCw, Download, Star, EyeOff, Eye, ExternalLink, Save, Plus, Loader2, Upload,
} from "lucide-react";

const card = "rounded-2xl border border-white/10 bg-surface/60 p-5";

function ArtworkUpload({ value, onChange }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) {
      setError("Image too large — max 8MB.");
      e.target.value = "";
      return;
    }
    setUploading(true);
    setError("");
    const form = new FormData();
    form.append("file", file);
    try {
      const res = await api.post("/admin/upload", form);
      onChange(res.data.url);
    } catch {
      setError("Upload failed. Try a smaller image (max 8MB) or a JPG/PNG/WEBP/GIF.");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  return (
    <div className="sm:col-span-2">
      <span className="text-[11px] uppercase tracking-widest text-white/40">Cover Artwork</span>
      <div className="mt-1 flex items-center gap-3">
        {value && (
          <img data-testid="artwork-preview" src={value} alt="artwork preview" className="h-16 w-16 shrink-0 rounded-lg border border-white/10 object-cover" />
        )}
        <div className="min-w-0 flex-1">
          <input
            data-testid="artwork-url-input"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder="Paste an image URL, or upload a file below"
            className="w-full rounded-lg border border-white/15 bg-ink/60 px-3 py-2 text-sm text-white outline-none focus:border-gold"
          />
          <div className="mt-2 flex items-center gap-2">
            <button
              type="button"
              data-testid="artwork-upload-button"
              onClick={() => inputRef.current?.click()}
              disabled={uploading}
              className="inline-flex items-center gap-2 rounded-full border border-gold/40 bg-gold/10 px-4 py-2 text-xs font-bold uppercase text-gold disabled:opacity-60"
            >
              {uploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
              {uploading ? "Uploading…" : "Upload Image"}
            </button>
            <input
              ref={inputRef}
              data-testid="artwork-file-input"
              type="file"
              accept="image/png,image/jpeg,image/webp,image/gif"
              className="hidden"
              onChange={handleFile}
            />
          </div>
          {error && <p data-testid="artwork-upload-error" className="mt-1 text-xs text-red-300">{error}</p>}
        </div>
      </div>
    </div>
  );
}

function StatCard({ label, value, sub }) {
  return (
    <div className={card} data-testid={`stat-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <p className="text-[11px] font-semibold uppercase tracking-widest text-white/40">{label}</p>
      <p className="mt-2 font-display text-3xl font-bold text-white">{value}</p>
      {sub && <p className="mt-1 text-xs text-gold">{sub}</p>}
    </div>
  );
}

/* ------------------------- DASHBOARD ------------------------- */
export function Dashboard() {
  const [s, setS] = useState(null);
  const [events, setEvents] = useState([]);
  useEffect(() => {
    api.get("/admin/analytics/summary").then((r) => setS(r.data.summary)).catch(() => {});
    api.get("/admin/release-events").then((r) => setEvents(r.data)).catch(() => {});
  }, []);
  return (
    <div>
      <h2 className="mb-6 font-display text-2xl font-bold uppercase tracking-tight text-white">Dashboard</h2>
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <StatCard label="Visitors" value={s?.pageViews ?? "—"} />
        <StatCard label="Play Starts" value={s?.playStarts ?? "—"} />
        <StatCard label="30s Listeners" value={s?.thirtySecond ?? "—"} sub={s ? `${s.thirtyRetention}% retention` : ""} />
        <StatCard label="Spotify Clicks" value={s?.ctaClicks ?? "—"} sub={s ? `${s.spotifyConversion}% conv.` : ""} />
      </div>
      <div className={`${card} mt-6`}>
        <h3 className="mb-4 font-display text-lg font-semibold text-white">New Release Events</h3>
        {events.length === 0 ? (
          <p className="text-sm text-white/40">No automatic release detections yet. Enable Spotify sync to populate this feed.</p>
        ) : (
          <ul className="space-y-3">
            {events.map((e) => (
              <li key={e.id} className="flex items-center gap-3 text-sm text-white/70">
                <img src={e.artwork} alt="" className="h-9 w-9 rounded object-cover" />
                <span className="font-medium text-white">{e.title}</span>
                <span className="text-white/40">· {e.detectedAt?.slice(0, 10)}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

/* ------------------------- RELEASES ------------------------- */
function EditRelease({ r, onDone }) {
  const [f, setF] = useState({
    appleMusicURL: r.appleMusicURL || "", youtubeMusicURL: r.youtubeMusicURL || "",
    youtubeURL: r.youtubeURL || "", amazonMusicURL: r.amazonMusicURL || "",
    deezerURL: r.deezerURL || "", tidalURL: r.tidalURL || "",
    artwork: r.artwork || "", description: r.description || "", seoDescription: r.seoDescription || "",
    spotifyURL: r.spotifyURL || "",
    story: r.story || "", lyrics: r.lyrics || "", genre: r.genre || "", isrc: r.isrc || "",
    active: r.active, hidden: r.hidden, upcoming: r.upcoming || false,
    releaseDate: r.releaseDate || "",
  });
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    await api.patch(`/admin/releases/${r.internalId}`, f).catch(() => {});
    setSaving(false);
    onDone();
  };
  const field = (k, label) => (
    <label className="block">
      <span className="text-[11px] uppercase tracking-widest text-white/40">{label}</span>
      <input value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })}
        className="mt-1 w-full rounded-lg border border-white/15 bg-ink/60 px-3 py-2 text-sm text-white outline-none focus:border-gold" />
    </label>
  );
  return (
    <div className="mt-3 grid gap-3 rounded-xl border border-white/10 bg-ink/40 p-4 sm:grid-cols-2">
      {field("appleMusicURL", "Apple Music URL")}
      {field("youtubeMusicURL", "YouTube Music URL")}
      {field("youtubeURL", "YouTube URL")}
      {field("amazonMusicURL", "Amazon Music URL")}
      {field("deezerURL", "Deezer URL")}
      {field("tidalURL", "Tidal URL")}
      <ArtworkUpload value={f.artwork} onChange={(v) => setF({ ...f, artwork: v })} />
      {field("seoDescription", "SEO Description")}
      {field("releaseDate", "Release Date (YYYY-MM-DD)")}
      {field("spotifyURL", "Spotify / Pre-Save URL")}
      {field("genre", "Genre (e.g. Reggaetón)")}
      {field("isrc", "ISRC")}
      <label className="block sm:col-span-2">
        <span className="text-[11px] uppercase tracking-widest text-white/40">Description</span>
        <textarea value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })}
          className="mt-1 w-full rounded-lg border border-white/15 bg-ink/60 px-3 py-2 text-sm text-white outline-none focus:border-gold" rows={2} />
      </label>
      <label className="block sm:col-span-2">
        <span className="text-[11px] uppercase tracking-widest text-white/40">Story behind the song (150–400 words · shown on the release page, indexed by Google)</span>
        <textarea data-testid="release-story" value={f.story} onChange={(e) => setF({ ...f, story: e.target.value })}
          className="mt-1 w-full rounded-lg border border-white/15 bg-ink/60 px-3 py-2 text-sm text-white outline-none focus:border-gold" rows={6} />
      </label>
      <label className="block sm:col-span-2">
        <span className="text-[11px] uppercase tracking-widest text-white/40">Lyrics (optional · only when approved)</span>
        <textarea value={f.lyrics} onChange={(e) => setF({ ...f, lyrics: e.target.value })}
          className="mt-1 w-full rounded-lg border border-white/15 bg-ink/60 px-3 py-2 text-sm text-white outline-none focus:border-gold" rows={4} />
      </label>
      <div className="flex gap-4 sm:col-span-2">
        <label className="flex items-center gap-2 text-sm text-white/70"><input type="checkbox" checked={f.active} onChange={(e) => setF({ ...f, active: e.target.checked })} /> Active</label>
        <label className="flex items-center gap-2 text-sm text-white/70"><input type="checkbox" checked={f.hidden} onChange={(e) => setF({ ...f, hidden: e.target.checked })} /> Hidden</label>
        <label className="flex items-center gap-2 text-sm text-white/70"><input type="checkbox" checked={f.upcoming} onChange={(e) => setF({ ...f, upcoming: e.target.checked })} /> Upcoming (countdown)</label>
      </div>
      <button data-testid="save-release" onClick={save} disabled={saving}
        className="inline-flex items-center gap-2 rounded-full bg-gold px-5 py-2.5 text-xs font-bold uppercase text-black">
        {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save
      </button>
    </div>
  );
}

export function ReleasesAdmin() {
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null);
  const [adding, setAdding] = useState(false);
  const load = () => api.get("/admin/releases").then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const feature = async (id) => { await api.post(`/admin/releases/${id}/feature`); load(); };
  const hide = async (id) => { await api.post(`/admin/releases/${id}/hide`); load(); };
  const publish = async (id) => {
    const res = await api.post(`/admin/releases/${id}/publish`).catch(() => null);
    if (res) alert(`Published! Notified ${res.data.emailsSent} fan(s) on the notify list.`);
    load();
  };

  return (
    <div>
      <div className="mb-6 flex items-center justify-between">
        <h2 className="font-display text-2xl font-bold uppercase tracking-tight text-white">Releases</h2>
        <button data-testid="add-release-toggle" onClick={() => setAdding(!adding)} className="inline-flex items-center gap-2 rounded-full border border-gold/40 bg-gold/10 px-4 py-2 text-xs font-bold uppercase text-gold">
          <Plus size={14} /> Manual Release
        </button>
      </div>
      {adding && <AddRelease onDone={() => { setAdding(false); load(); }} />}
      <div className="space-y-3">
        {rows.map((r) => (
          <div key={r.internalId} className={card} data-testid={`admin-release-${r.slug}`}>
            <div className="flex items-center gap-4">
              <img src={r.artwork} alt="" className="h-14 w-14 rounded-lg object-cover" />
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-display text-base font-semibold text-white">{r.title}</span>
                  {r.featured && <span className="rounded-full bg-gold px-2 py-0.5 text-[10px] font-bold uppercase text-black">Featured</span>}
                  {r.upcoming && <span className="rounded-full bg-gold/20 px-2 py-0.5 text-[10px] font-bold uppercase text-gold">Upcoming</span>}
                  {r.hidden && <span className="rounded-full bg-red-500/20 px-2 py-0.5 text-[10px] font-bold uppercase text-red-300">Hidden</span>}
                  {r.automaticallyImported ? <span className="text-[10px] uppercase text-white/40">Spotify Sync</span> : <span className="text-[10px] uppercase text-white/40">Manual</span>}
                </div>
                <p className="text-xs text-white/40">{r.releaseType} · {r.releaseDate} · {r.spotifyTrackId ? `Spotify ✓` : "No Spotify ID"}</p>
              </div>
              <div className="flex shrink-0 gap-2">
                {r.upcoming && (
                  <button data-testid={`publish-${r.slug}`} onClick={() => publish(r.internalId)} title="Publish & notify list" className="rounded-full border border-gold/40 bg-gold/10 px-3 py-2 text-xs font-bold uppercase text-gold hover:bg-gold hover:text-black">Publish &amp; Notify</button>
                )}
                <button data-testid={`feature-${r.slug}`} onClick={() => feature(r.internalId)} title="Feature" className="rounded-full border border-white/15 p-2 text-white/70 hover:text-gold"><Star size={16} /></button>
                <button data-testid={`hide-${r.slug}`} onClick={() => hide(r.internalId)} title="Hide/Show" className="rounded-full border border-white/15 p-2 text-white/70 hover:text-gold">{r.hidden ? <Eye size={16} /> : <EyeOff size={16} />}</button>
                <a href={`/music/${r.slug}`} target="_blank" rel="noreferrer" title="View page" className="rounded-full border border-white/15 p-2 text-white/70 hover:text-gold"><ExternalLink size={16} /></a>
                <button onClick={() => setEditing(editing === r.internalId ? null : r.internalId)} className="rounded-full border border-white/15 px-3 py-2 text-xs font-semibold uppercase text-white/70 hover:text-gold">Edit</button>
              </div>
            </div>
            {editing === r.internalId && <EditRelease r={r} onDone={() => { setEditing(null); load(); }} />}
          </div>
        ))}
      </div>
    </div>
  );
}

function AddRelease({ onDone }) {
  const [f, setF] = useState({ title: "", artwork: "", releaseType: "single", releaseDate: "", spotifyURL: "", spotifyURI: "", appleMusicURL: "", upcoming: false });
  const [saving, setSaving] = useState(false);
  const save = async () => {
    if (!f.title) return;
    setSaving(true);
    await api.post("/admin/releases", f).catch(() => {});
    setSaving(false);
    onDone();
  };
  const field = (k, label, ph = "") => (
    <label className="block">
      <span className="text-[11px] uppercase tracking-widest text-white/40">{label}</span>
      <input value={f[k]} placeholder={ph} onChange={(e) => setF({ ...f, [k]: e.target.value })}
        className="mt-1 w-full rounded-lg border border-white/15 bg-ink/60 px-3 py-2 text-sm text-white outline-none focus:border-gold" />
    </label>
  );
  return (
    <div className={`${card} mb-4 grid gap-3 sm:grid-cols-2`}>
      {field("title", "Title *")}
      <label className="block">
        <span className="text-[11px] uppercase tracking-widest text-white/40">Type</span>
        <select value={f.releaseType} onChange={(e) => setF({ ...f, releaseType: e.target.value })} className="mt-1 w-full rounded-lg border border-white/15 bg-ink/60 px-3 py-2 text-sm text-white">
          <option value="single">single</option><option value="ep">ep</option><option value="album">album</option>
        </select>
      </label>
      <ArtworkUpload value={f.artwork} onChange={(v) => setF({ ...f, artwork: v })} />
      {field("releaseDate", "Release Date", "YYYY-MM-DD")}
      {field("spotifyURL", "Spotify URL")}
      {field("spotifyURI", "Spotify URI", "spotify:track:...")}
      {field("appleMusicURL", "Apple Music URL")}
      <label className="flex items-center gap-2 text-sm text-white/70 sm:col-span-2">
        <input type="checkbox" checked={f.upcoming} onChange={(e) => setF({ ...f, upcoming: e.target.checked })} /> Upcoming release (show countdown to Release Date)
      </label>
      <div className="sm:col-span-2">
        <button data-testid="save-manual-release" onClick={save} disabled={saving} className="inline-flex items-center gap-2 rounded-full bg-gold px-5 py-2.5 text-xs font-bold uppercase text-black">
          {saving ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />} Create Release
        </button>
      </div>
    </div>
  );
}

/* ------------------------- ANALYTICS ------------------------- */
export function AnalyticsAdmin() {
  const [data, setData] = useState(null);
  const [filters, setFilters] = useState({ from: "", to: "", source: "", campaign: "" });
  const load = () => {
    const qs = new URLSearchParams(Object.fromEntries(Object.entries(filters).filter(([, v]) => v))).toString();
    api.get(`/admin/analytics/summary${qs ? `?${qs}` : ""}`).then((r) => setData(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []); // eslint-disable-line
  const s = data?.summary;
  const maxFunnel = data ? Math.max(...data.funnel.map((f) => f.value), 1) : 1;
  return (
    <div>
      <h2 className="mb-6 font-display text-2xl font-bold uppercase tracking-tight text-white">Analytics</h2>
      <div className={`${card} mb-6 flex flex-wrap items-end gap-3`}>
        {["from", "to"].map((k) => (
          <label key={k} className="block">
            <span className="text-[11px] uppercase tracking-widest text-white/40">{k}</span>
            <input type="date" value={filters[k]} onChange={(e) => setFilters({ ...filters, [k]: e.target.value })} className="mt-1 block rounded-lg border border-white/15 bg-ink/60 px-3 py-2 text-sm text-white" />
          </label>
        ))}
        <label className="block"><span className="text-[11px] uppercase tracking-widest text-white/40">Source</span>
          <input value={filters.source} onChange={(e) => setFilters({ ...filters, source: e.target.value })} placeholder="tiktok" className="mt-1 block rounded-lg border border-white/15 bg-ink/60 px-3 py-2 text-sm text-white" /></label>
        <label className="block"><span className="text-[11px] uppercase tracking-widest text-white/40">Campaign</span>
          <input value={filters.campaign} onChange={(e) => setFilters({ ...filters, campaign: e.target.value })} className="mt-1 block rounded-lg border border-white/15 bg-ink/60 px-3 py-2 text-sm text-white" /></label>
        <button data-testid="apply-filters" onClick={load} className="rounded-full bg-gold px-5 py-2.5 text-xs font-bold uppercase text-black">Apply</button>
      </div>

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <StatCard label="Visitors" value={s?.pageViews ?? 0} />
        <StatCard label="Release Views" value={s?.releaseViews ?? 0} />
        <StatCard label="Play Starts" value={s?.playStarts ?? 0} />
        <StatCard label="10s" value={s?.tenSecond ?? 0} />
        <StatCard label="30s" value={s?.thirtySecond ?? 0} sub={s ? `${s.thirtyRetention}% retention` : ""} />
        <StatCard label="50%" value={s?.fiftyPercent ?? 0} />
        <StatCard label="90%" value={s?.ninetyPercent ?? 0} />
        <StatCard label="Completions" value={s?.trackCompletions ?? 0} />
        <StatCard label="Spotify Clicks" value={s?.ctaClicks ?? 0} sub={s ? `${s.spotifyConversion}% conv.` : ""} />
        <StatCard label="Apple Music" value={s?.appleMusicClicks ?? 0} />
        <StatCard label="YouTube Music" value={s?.youtubeMusicClicks ?? 0} />
        <StatCard label="Email Signups" value={s?.emailSignups ?? 0} />
      </div>

      <div className={`${card} mt-6`}>
        <h3 className="mb-4 font-display text-lg font-semibold text-white">Follow / Favorite Conversion</h3>
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
          <StatCard label="Spotify Follow Clicks" value={s?.spotifyFollowClicks ?? 0} />
          <StatCard label="Apple Music Auth Started" value={s?.appleMusicAuthStarted ?? 0} />
          <StatCard label="Apple Music Favorited" value={s?.appleMusicFavorited ?? 0} />
          <StatCard label="Apple Music Page Opened" value={s?.appleMusicPageOpened ?? 0} sub="fallback" />
        </div>
      </div>

      <div className={`${card} mt-6`}>
        <h3 className="mb-4 font-display text-lg font-semibold text-white">Conversion Funnel</h3>
        <div className="space-y-2">
          {data?.funnel.map((f) => (
            <div key={f.stage} className="flex items-center gap-3">
              <span className="w-28 shrink-0 text-xs uppercase tracking-wide text-white/50">{f.stage}</span>
              <div className="h-6 flex-1 overflow-hidden rounded-full bg-white/5">
                <div className="h-full rounded-full bg-gold" style={{ width: `${(f.value / maxFunnel) * 100}%` }} />
              </div>
              <span className="w-10 text-right text-sm font-semibold text-white">{f.value}</span>
            </div>
          ))}
        </div>
      </div>

      <div className={`${card} mt-6 overflow-x-auto`}>
        <h3 className="mb-4 font-display text-lg font-semibold text-white">Release Performance</h3>
        <table className="w-full min-w-[720px] text-left text-sm">
          <thead><tr className="text-[11px] uppercase tracking-widest text-white/40">
            <th className="pb-2">Title</th><th>Views</th><th>Plays</th><th>10s</th><th>30s</th><th>Ret%</th><th>50%</th><th>90%</th><th>Done</th><th>Spotify</th><th>Source</th>
          </tr></thead>
          <tbody>
            {data?.releaseTable.map((t) => (
              <tr key={t.releaseId} className="border-t border-white/5 text-white/80">
                <td className="py-2 font-medium text-white">{t.title}</td>
                <td>{t.views}</td><td>{t.playStarts}</td><td>{t.tenSecond}</td><td>{t.thirtySecond}</td>
                <td>{t.thirtyRetention}%</td><td>{t.fiftyPercent}</td><td>{t.ninetyPercent}</td><td>{t.completions}</td>
                <td>{t.spotifyClicks}</td><td className="capitalize">{t.topSource}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {data?.releaseTable.length === 0 && <p className="text-sm text-white/40">No playback data yet.</p>}
      </div>
    </div>
  );
}

/* ------------------------- CAMPAIGNS ------------------------- */
export function CampaignsAdmin() {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/admin/campaigns").then((r) => setRows(r.data)).catch(() => {}); }, []);
  return (
    <div>
      <h2 className="mb-6 font-display text-2xl font-bold uppercase tracking-tight text-white">Campaigns</h2>
      <div className={`${card} overflow-x-auto`}>
        <table className="w-full min-w-[640px] text-left text-sm">
          <thead><tr className="text-[11px] uppercase tracking-widest text-white/40">
            <th className="pb-2">Campaign</th><th>Source</th><th>Song</th><th>Visits</th><th>Plays</th><th>30s</th><th>Spotify</th><th>Conv%</th>
          </tr></thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i} className="border-t border-white/5 text-white/80">
                <td className="py-2 font-medium text-white">{r.campaign}</td>
                <td className="capitalize">{r.source}</td><td>{r.song}</td><td>{r.visits}</td>
                <td>{r.playStarts}</td><td>{r.thirtySecond}</td><td>{r.spotifyClicks}</td><td>{r.conversion}%</td>
              </tr>
            ))}
          </tbody>
        </table>
        {rows.length === 0 && <p className="text-sm text-white/40">No campaign traffic yet. Share links with ?utm_source=tiktok&utm_campaign=... to populate this.</p>}
      </div>
    </div>
  );
}

/* ------------------------- SPOTIFY SYNC ------------------------- */
export function SpotifySync() {
  const [status, setStatus] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const load = () => api.get("/admin/spotify/status").then((r) => setStatus(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const sync = async () => {
    setSyncing(true);
    await api.post("/admin/spotify/sync").catch(() => {});
    setSyncing(false);
    load();
  };
  const Row = ({ k, v }) => (<div className="flex justify-between border-b border-white/5 py-2 text-sm"><span className="text-white/50">{k}</span><span className="text-white">{v}</span></div>);
  return (
    <div>
      <h2 className="mb-6 font-display text-2xl font-bold uppercase tracking-tight text-white">Spotify Sync</h2>
      {!status?.credentialsPresent && (
        <div className="mb-6 rounded-2xl border border-gold/30 bg-gold/10 p-4 text-sm text-gold">
          Spotify API credentials not configured. Add SPOTIFY_CLIENT_ID and SPOTIFY_CLIENT_SECRET to enable automatic catalog sync. The public site keeps working from the database in the meantime.
        </div>
      )}
      <div className={card}>
        <Row k="Connection Status" v={status?.connected ? "Connected ✓" : "Not connected"} />
        <Row k="Last Successful Sync" v={status?.lastSuccess ? new Date(status.lastSuccess).toLocaleString() : "—"} />
        <Row k="Next Scheduled Sync" v={status?.nextScheduledSync ? new Date(status.nextScheduledSync).toLocaleString() : `Every ${status?.syncFrequencyHours || 6}h`} />
        <Row k="Releases Found" v={status?.found ?? 0} />
        <Row k="New Releases Imported" v={status?.imported ?? 0} />
        <Row k="Last Error" v={status?.lastError || "None"} />
        <button data-testid="sync-spotify-now" onClick={sync} disabled={syncing} className="mt-5 inline-flex items-center gap-2 rounded-full bg-gold px-6 py-3 text-sm font-bold uppercase text-black disabled:opacity-60">
          {syncing ? <Loader2 size={16} className="animate-spin" /> : <RefreshCw size={16} />} Sync Spotify Now
        </button>
      </div>
    </div>
  );
}

/* ------------------------- FAN LIST ------------------------- */
export function FanList() {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/admin/fans").then((r) => setRows(r.data)).catch(() => {}); }, []);
  const exportCsv = () => window.open(`${API}/admin/fans/export`, "_blank");
  return (
    <div>
      <div className="mb-6 flex items-center justify-between">
        <h2 className="font-display text-2xl font-bold uppercase tracking-tight text-white">Fan List <span className="text-gold">({rows.length})</span></h2>
        <button data-testid="export-fans" onClick={exportCsv} className="inline-flex items-center gap-2 rounded-full border border-gold/40 bg-gold/10 px-4 py-2 text-xs font-bold uppercase text-gold"><Download size={14} /> Export CSV</button>
      </div>
      <div className={`${card} overflow-x-auto`}>
        <table className="w-full min-w-[520px] text-left text-sm">
          <thead><tr className="text-[11px] uppercase tracking-widest text-white/40"><th className="pb-2">Email</th><th>Signed Up</th><th>Drop</th><th>Source</th><th>Campaign</th></tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-t border-white/5 text-white/80">
                <td className="py-2 text-white">{r.email}</td>
                <td>{r.consentAt?.slice(0, 10)}</td><td className="text-gold">{r.releaseTitle || "—"}</td><td className="capitalize">{r.utmSource || "—"}</td><td>{r.utmCampaign || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {rows.length === 0 && <p className="text-sm text-white/40">No signups yet.</p>}
      </div>
    </div>
  );
}

/* ------------------------- SETTINGS ------------------------- */
export function SettingsAdmin() {
  const [s, setS] = useState(null);
  const [saved, setSaved] = useState(false);
  useEffect(() => { api.get("/admin/settings").then((r) => setS(r.data)).catch(() => {}); }, []);
  if (!s) return null;
  const setField = (k, v) => setS({ ...s, [k]: v });
  const setNested = (grp, k, v) => setS({ ...s, [grp]: { ...(s[grp] || {}), [k]: v } });
  const save = async () => { await api.put("/admin/settings", s).catch(() => {}); setSaved(true); setTimeout(() => setSaved(false), 1500); };
  const inp = (val, on, ph = "") => (<input value={val || ""} placeholder={ph} onChange={(e) => on(e.target.value)} className="mt-1 w-full rounded-lg border border-white/15 bg-ink/60 px-3 py-2 text-sm text-white outline-none focus:border-gold" />);
  const lbl = (t) => <span className="text-[11px] uppercase tracking-widest text-white/40">{t}</span>;
  return (
    <div>
      <h2 className="mb-6 font-display text-2xl font-bold uppercase tracking-tight text-white">Settings</h2>
      <div className="space-y-6">
        <div className={card}>
          <h3 className="mb-4 font-display text-lg font-semibold text-white">Artist</h3>
          <div className="grid gap-3 sm:grid-cols-2">
            <label>{lbl("Artist Name")}{inp(s.artistName, (v) => setField("artistName", v))}</label>
            <label>{lbl("Spotify Artist ID")}{inp(s.spotifyArtistId, (v) => setField("spotifyArtistId", v))}</label>
            <label>{lbl("Artist Image URL")}{inp(s.artistImage, (v) => setField("artistImage", v))}</label>
            <label>{lbl("Genre Line")}{inp(s.genreLine, (v) => setField("genreLine", v))}</label>
            <label className="sm:col-span-2">{lbl("Bio")}<textarea value={s.bio || ""} onChange={(e) => setField("bio", e.target.value)} rows={2} className="mt-1 w-full rounded-lg border border-white/15 bg-ink/60 px-3 py-2 text-sm text-white outline-none focus:border-gold" /></label>
          </div>
        </div>
        <div className={card}>
          <h3 className="mb-4 font-display text-lg font-semibold text-white">Social</h3>
          <div className="grid gap-3 sm:grid-cols-2">
            {["tiktok", "instagram", "youtube", "facebook"].map((k) => <label key={k}>{lbl(k)}{inp(s.socials?.[k], (v) => setNested("socials", k, v))}</label>)}
          </div>
        </div>
        <div className={card}>
          <h3 className="mb-4 font-display text-lg font-semibold text-white">Streaming</h3>
          <div className="grid gap-3 sm:grid-cols-2">
            {["spotify", "apple_music", "youtube_music", "amazon_music", "deezer", "tidal"].map((k) => <label key={k}>{lbl(k.replace("_", " "))}{inp(s.streaming?.[k], (v) => setNested("streaming", k, v))}</label>)}
          </div>
        </div>
        <div className={card}>
          <h3 className="mb-4 font-display text-lg font-semibold text-white">Release Settings</h3>
          <div className="grid gap-4 sm:grid-cols-2">
            <label className="flex items-center gap-2 text-sm text-white/70"><input type="checkbox" checked={s.autoSync} onChange={(e) => setField("autoSync", e.target.checked)} /> Auto Spotify Sync</label>
            <label>{lbl("Sync Frequency (hours)")}<input type="number" value={s.syncFrequencyHours} onChange={(e) => setField("syncFrequencyHours", Number(e.target.value))} className="mt-1 w-full rounded-lg border border-white/15 bg-ink/60 px-3 py-2 text-sm text-white" /></label>
            <label>{lbl("NEW Badge Duration (days)")}<input type="number" value={s.newBadgeDays} onChange={(e) => setField("newBadgeDays", Number(e.target.value))} className="mt-1 w-full rounded-lg border border-white/15 bg-ink/60 px-3 py-2 text-sm text-white" /></label>
            <label>{lbl("Featured Mode")}
              <select value={s.featuredMode} onChange={(e) => setField("featuredMode", e.target.value)} className="mt-1 w-full rounded-lg border border-white/15 bg-ink/60 px-3 py-2 text-sm text-white">
                <option value="automatic">automatic</option><option value="manual">manual</option>
              </select>
            </label>
            <label className="flex items-center gap-2 text-sm text-white/70"><input type="checkbox" checked={s.analyticsEnabled} onChange={(e) => setField("analyticsEnabled", e.target.checked)} /> Analytics Tracking</label>
          </div>
        </div>
        <GrowthSettings s={s} setField={setField} setNested={setNested} />
        <button data-testid="save-settings" onClick={save} className="inline-flex items-center gap-2 rounded-full bg-gold px-6 py-3 text-sm font-bold uppercase text-black">
          <Save size={16} /> {saved ? "Saved ✓" : "Save Settings"}
        </button>
      </div>
    </div>
  );
}
